import { memo, useEffect, useState } from "react";
import { type CadTabProps } from "~/features/productDetails/components/PdpTabs/CadTab/CadTab";

import { userIsLoggedIn } from "~/domains/user/utils/roles";
import { Button, Stack, styled } from "~/common/mui";
import { isLoginPopupInitiallyOpen } from "~/features/auth/login/loginUtils";
import { Trans, useTranslation } from "react-i18next";
import { requestCadNotification } from "~/features/productDetails/components/PdpTabs/CadTab/api/requestCadNotification";
import { trackPdpRequestCADNotify, trackPdpRequestCADNotifyResult } from "~/app/analytics/ga/ga";
import MailIcon from "@mui/icons-material/Mail";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useDispatch } from "react-redux";
import { openAuthOverlay } from "~/features/registration/components/redux/AuthActions";

const EmptyCadTab = (props: CadTabProps) => {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(props.product.customerRequestedCADAvailability);
  const [submitting, setSubmitting] = useState(false);

  const isLoggedIn = userIsLoggedIn();
  const dispatch = useDispatch();

  useEffect(() => {
    if (isLoginPopupInitiallyOpen()) {
      dispatch(openAuthOverlay("login", { alternativeGAAction: "Sign In CAD Tab" }));
    }
  }, [dispatch]);

  const subscribe = async () => {
    trackPdpRequestCADNotify();
    setSubmitting(true);
    try {
      await requestCadNotification(props.product.code);
      trackPdpRequestCADNotifyResult(true);
    } catch {
      trackPdpRequestCADNotifyResult(false);
    }
    setSubmitting(false);
    setSubmitted(true);
  };

  const cta = (
    <Button
      startIcon={<MailIcon />}
      onClick={
        isLoggedIn
          ? subscribe
          : () => dispatch(openAuthOverlay("login", { alternativeGAAction: "Sign In CAD Tab" }))
      }
      loading={submitting}
    >
      {t(`page.pdp.cad.sorry.cta.${isLoggedIn ? "notify" : "login"}`)}
    </Button>
  );

  let content;
  if (!submitted) {
    content = (
      <ContentContainer gap={2} direction="column">
        <Title>{t("page.pdp.cad.sorry.title")}</Title>
        <Text>{t(`page.pdp.cad.sorry.content.${isLoggedIn ? "user" : "anonymous"}`)}</Text>
        {cta}
      </ContentContainer>
    );
  } else {
    const designation = props.product.designation;
    content = (
      <ContentContainer gap={4} direction="column">
        <CheckCircleIcon className={"f-green"} sx={{ fontSize: 64 }} />
        <Trans i18nKey={"page.pdp.cad.sorry.success"}>
          Thank you for your interest. We will notify you as soon as the CAD file for <b>{{ designation }}</b> is
          available.
        </Trans>
      </ContentContainer>
    );
  }

  return (
    <>
      <Root>{content}</Root>
    </>
  );
};

const Root = styled("div")(({ theme }) => ({
  height: theme.spacing(24.25),
  display: "flex",
  justifyContent: "center",
  alignItems: "center",

  [theme.breakpoints.up("desktop-s")]: {
    height: theme.spacing(68),
  },
}));

const ContentContainer = styled(Stack)(({ theme }) => ({
  alignItems: "center",
  textAlign: "center",

  [theme.breakpoints.up("tablet")]: {
    width: theme.spacing(59),
  },

  [theme.breakpoints.up("desktop-s")]: {
    width: theme.spacing(72),
  },
}));

const Title = styled("h2")(({ theme }) => ({
  ...theme.typography.headlineSmall,
  fontWeight: "bold",
  margin: 0,
}));

const Text = styled("p")(({ theme }) => ({
  ...theme.typography.titleMedium,
  whiteSpace: "pre-line",
  margin: 0,
}));

export default memo(EmptyCadTab);
